import React, { useMemo, useState } from "react";
import {
  buildCustomOrderAnswers,
  computeCustomOrderUnitPrice,
  customOrderHasSizes,
  getSelectedCustomOrderSize,
  isCustomOrderOptionAvailableForSize,
  normalizeCustomOrderFields,
  resolveCustomOrderOptionPriceForSize,
  sortCustomOrderSelectOptions,
  validateCustomOrderForm,
  visibleCustomOrderOptions,
} from "../../utils/customOrderForm";

export default function CustomOrderSection({
  sectionId,
  settings = {},
  frame,
  fg,
  titlePx,
  subtitlePx,
  headingAlign,
  primaryColor,
  secondaryColor,
  showStorefrontCart,
  menuOnly,
  onAddToCart,
}) {
  const st = settings || {};
  const fields = useMemo(() => normalizeCustomOrderFields(st.fields), [st.fields]);
  const [formState, setFormState] = useState({});
  const [error, setError] = useState("");

  const hasSizes = useMemo(() => customOrderHasSizes(fields), [fields]);
  const selectedSize = useMemo(
    () => getSelectedCustomOrderSize(fields, formState),
    [fields, formState]
  );

  const answersPreview = useMemo(
    () => buildCustomOrderAnswers(fields, formState),
    [fields, formState]
  );
  const unitPrice = useMemo(
    () => computeCustomOrderUnitPrice(st, answersPreview),
    [st, answersPreview]
  );

  const updateText = (fieldId, value) => {
    setFormState((prev) => ({ ...prev, [fieldId]: { text: value } }));
  };

  const updateSelect = (fieldId, optionId, field) => {
    setFormState((prev) => {
      const next = { ...prev, [fieldId]: { optionId } };
      if (field?.role === "size" && hasSizes) {
        for (const f of fields) {
          if (f.type !== "checkbox_group") continue;
          const checked = Array.isArray(prev[f.id]?.checked) ? prev[f.id].checked : [];
          const valid = checked.filter((oid) => {
            const opt = (f.options || []).find((o) => String(o.id) === String(oid));
            return (
              opt &&
              isCustomOrderOptionAvailableForSize(opt, getSelectedCustomOrderSize(fields, next), hasSizes)
            );
          });
          next[f.id] = { checked: valid };
        }
      }
      return next;
    });
  };

  const toggleCheckbox = (fieldId, optionId, checked) => {
    setFormState((prev) => {
      const cur = Array.isArray(prev[fieldId]?.checked) ? prev[fieldId].checked : [];
      const next = checked
        ? [...new Set([...cur, optionId])]
        : cur.filter((x) => String(x) !== String(optionId));
      return { ...prev, [fieldId]: { checked: next } };
    });
  };

  const handleSubmit = () => {
    const missing = validateCustomOrderForm(fields, formState);
    if (missing.length) {
      setError(`يرجى تعبئة: ${missing.join("، ")}`);
      return;
    }
    setError("");
    const answers = buildCustomOrderAnswers(fields, formState);
    onAddToCart({
      sectionId,
      settings: st,
      answers,
      unitPrice: computeCustomOrderUnitPrice(st, answers),
    });
    setFormState({});
  };

  if (st.visible === false) return null;

  const canOrder = showStorefrontCart && !menuOnly;

  return (
    <section
      className="catalog-custom-order-section"
      style={{
        borderRadius: frame?.radius,
        border: frame?.border,
        background: st?.style?.backgroundColor || "#ffffff",
        color: fg || st?.style?.textColor || "#0f172a",
        padding: frame?.pad,
        direction: "rtl",
      }}
    >
      {st.title ? (
        <div style={{ textAlign: headingAlign || "start", marginBottom: 8 }}>
          <div style={{ fontWeight: 950, fontSize: titlePx }}>{st.title}</div>
          {st.subtitle ? (
            <div style={{ marginTop: 6, fontSize: subtitlePx, opacity: 0.85, lineHeight: 1.6 }}>
              {st.subtitle}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="catalog-custom-order-form" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {fields.map((field) => (
          <div key={field.id} className="catalog-custom-order-field">
            <label
              style={{
                display: "block",
                fontWeight: 800,
                fontSize: 14,
                marginBottom: 8,
                color: "inherit",
              }}
            >
              {field.label}
              {field.type !== "checkbox_group" ? (
                <span style={{ color: "#dc2626", marginInlineStart: 4 }}>*</span>
              ) : null}
            </label>

            {field.type === "text" ? (
              <input
                type="text"
                className="catalog-custom-order-input"
                value={formState[field.id]?.text ?? ""}
                onChange={(e) => updateText(field.id, e.target.value)}
                disabled={!canOrder}
                style={{
                  width: "100%",
                  padding: "12px 14px",
                  borderRadius: 12,
                  border: "1px solid rgba(15,23,42,0.12)",
                  fontSize: 14,
                  fontWeight: 600,
                  boxSizing: "border-box",
                }}
              />
            ) : null}

            {field.type === "select" ? (
              <select
                className="catalog-custom-order-select"
                value={formState[field.id]?.optionId ?? ""}
                onChange={(e) => updateSelect(field.id, e.target.value, field)}
                disabled={!canOrder}
                style={{
                  width: "100%",
                  padding: "12px 14px",
                  borderRadius: 12,
                  border: "1px solid rgba(15,23,42,0.12)",
                  fontSize: 14,
                  fontWeight: 700,
                  background: "#fff",
                  boxSizing: "border-box",
                }}
              >
                <option value="">— اختر —</option>
                {sortCustomOrderSelectOptions(field.options).map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.label}
                    {Number(opt.price) > 0 ? ` — ₪${Number(opt.price)}` : ""}
                  </option>
                ))}
              </select>
            ) : null}

            {field.type === "checkbox_group" ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {visibleCustomOrderOptions(field.options)
                  .filter((opt) =>
                    !hasSizes ||
                    isCustomOrderOptionAvailableForSize(opt, selectedSize, hasSizes)
                  )
                  .map((opt) => {
                    const checked = (formState[field.id]?.checked || []).includes(opt.id);
                    const displayPrice =
                      (resolveCustomOrderOptionPriceForSize(opt, selectedSize, hasSizes) ??
                        Number(opt.price)) || 0;
                    return (
                      <label
                        key={opt.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 12,
                          padding: "12px 14px",
                          borderRadius: 12,
                          border: `2px solid ${checked ? primaryColor : "#e5e7eb"}`,
                          background: checked ? `${primaryColor}08` : "#fff",
                          cursor: canOrder ? "pointer" : "not-allowed",
                          fontWeight: 700,
                          fontSize: 14,
                        }}
                      >
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={!canOrder}
                            onChange={(e) => toggleCheckbox(field.id, opt.id, e.target.checked)}
                          />
                          <span>{opt.label}</span>
                        </span>
                        {displayPrice > 0 ? (
                          <span style={{ color: primaryColor, fontWeight: 800 }}>+₪{displayPrice}</span>
                        ) : null}
                      </label>
                    );
                  })}
                {hasSizes && !selectedSize ? (
                  <p style={{ margin: 0, fontSize: 12, color: "#64748b", fontWeight: 700 }}>
                    اختر الحجم أولاً لعرض الإضافات المتاحة
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
        ))}
      </div>

      {error ? (
        <p style={{ margin: "12px 0 0", color: "#dc2626", fontWeight: 700, fontSize: 13 }}>{error}</p>
      ) : null}

      {canOrder && fields.length > 0 ? (
        <div
          style={{
            marginTop: 18,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div style={{ fontWeight: 900, fontSize: 18, color: primaryColor }}>
            {unitPrice > 0 ? `₪${unitPrice}` : null}
          </div>
          <button
            type="button"
            className="catalog-btn"
            onClick={handleSubmit}
            style={{
              background: primaryColor,
              color: secondaryColor,
              border: "none",
              padding: "14px 22px",
              borderRadius: 12,
              fontWeight: 900,
              fontSize: 15,
              cursor: "pointer",
            }}
          >
            {st.submit_label || "أضف إلى السلة"}
          </button>
        </div>
      ) : null}
    </section>
  );
}
